import { white } from '@kmns-internal/furikake/tokens'
import { MoreHoriz } from '@mui/icons-material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material'
import {
  ErrorCodeEnum,
  MasterFieldDef,
  MasterFieldTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { UsedInTemplateErrorDialog } from 'src/features/masters/components/UsedInTemplateErrorDialog/UsedInTemplateErrorDialog'
import { gray } from 'src/features/muikds/kdsColors'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import {
  ErrorTemplateRef,
  HTTPErrorWithTemplates,
  isHTTPError,
} from 'src/state/middleware/saga/handleHttpError'
import { useMutationDeleteMasterFieldDef } from '../../api/useMutationDeleteMasterFieldDef'
import { MasterFieldDefEditNameDrawer } from '../MasterFieldDefEditNameDrawer/MasterFieldDefEditNameDrawer'

const menuButtonTableCellWidth = 48
const fieldDefNameTableCellMaxWidth = 280

const fieldTypeLabels: Record<MasterFieldTypeEnum, string> = {
  [MasterFieldTypeEnum.Number]: '数値',
  [MasterFieldTypeEnum.Text]: 'テキスト',
}

const tableCellStyle = {
  backgroundColor: white,
} as const satisfies SxProps<Theme>

type Props = {
  masterUuid: string
  fieldDefs: MasterFieldDef[]
}

export const MasterFieldDefList: FC<Props> = ({ masterUuid, fieldDefs }) => {
  const existingFieldDefs = fieldDefs.map(fd => ({
    name: fd.name,
    code: fd.code,
  }))

  // 項目名編集ドロワの状態を一覧側で一元管理し、ドロワは1つだけ描画する
  const [editingFieldDef, setEditingFieldDef] = useState<MasterFieldDef | null>(
    null,
  )
  const openEditNameDrawer = useCallback(
    (fieldDef: MasterFieldDef) => setEditingFieldDef(fieldDef),
    [],
  )
  const closeEditNameDrawer = useCallback(() => setEditingFieldDef(null), [])

  return (
    <>
      <TableContainer sx={{ height: '100%' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={tableCellStyle}>
                <Typography sx={{ fontWeight: 700 }}>項目名</Typography>
              </TableCell>
              <TableCell sx={tableCellStyle}>
                <Typography sx={{ fontWeight: 700 }}>形式</Typography>
              </TableCell>
              <TableCell
                sx={[tableCellStyle, { width: menuButtonTableCellWidth }]}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {fieldDefs.map(fieldDef => (
              <FieldDefRow
                key={fieldDef.id}
                masterUuid={masterUuid}
                fieldDef={fieldDef}
                onEditName={openEditNameDrawer}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {editingFieldDef && (
        <MasterFieldDefEditNameDrawer
          open={editingFieldDef !== null}
          closeDrawer={closeEditNameDrawer}
          masterUuid={masterUuid}
          fieldDef={editingFieldDef}
          existingFieldDefs={existingFieldDefs}
        />
      )}
    </>
  )
}

const FieldDefRow: FC<{
  masterUuid: string
  fieldDef: MasterFieldDef
  onEditName: (fieldDef: MasterFieldDef) => void
}> = ({ masterUuid, fieldDef, onEditName }) => {
  return (
    <TableRow hover>
      <TableCell>
        <Typography sx={{ maxWidth: fieldDefNameTableCellMaxWidth }}>
          {fieldDef.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography>{fieldTypeLabels[fieldDef.fieldType]}</Typography>
      </TableCell>
      <TableCell sx={{ width: menuButtonTableCellWidth }}>
        <MenuButton
          masterUuid={masterUuid}
          fieldDef={fieldDef}
          onEditName={onEditName}
        />
      </TableCell>
    </TableRow>
  )
}

const MenuButton: FC<{
  masterUuid: string
  fieldDef: MasterFieldDef
  onEditName: (fieldDef: MasterFieldDef) => void
}> = ({ masterUuid, fieldDef, onEditName }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }, [])
  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null)
  }, [])

  const handleEditName = useCallback(() => {
    closeMenu()
    onEditName(fieldDef)
  }, [closeMenu, onEditName, fieldDef])

  const [usedInTemplateErrorTemplates, setUsedInTemplateErrorTemplates] =
    useState<Array<ErrorTemplateRef> | null>(null)
  const closeUsedInTemplateError = useCallback(
    () => setUsedInTemplateErrorTemplates(null),
    [],
  )

  const handleUsedInTemplateError = useCallback(
    (templates: Array<ErrorTemplateRef>) => {
      closeMenu()
      setUsedInTemplateErrorTemplates(templates)
    },
    [closeMenu],
  )

  return (
    <>
      <IconButton
        size="small"
        onClick={openMenu}
        aria-label={`項目メニューを開く`}
      >
        <MoreHoriz sx={{ color: gray[9] }} />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditName} sx={{ width: 220 }}>
          <ListItemIcon>
            <EditOutlinedIcon />
          </ListItemIcon>
          <ListItemText>項目名を編集</ListItemText>
        </MenuItem>
        <DeleteMenuItem
          masterUuid={masterUuid}
          fieldDef={fieldDef}
          onUsedInTemplateError={handleUsedInTemplateError}
        />
      </Menu>
      <UsedInTemplateErrorDialog
        open={usedInTemplateErrorTemplates !== null}
        templates={usedInTemplateErrorTemplates ?? []}
        onClose={closeUsedInTemplateError}
      />
    </>
  )
}

const DeleteMenuItem: FC<{
  masterUuid: string
  fieldDef: MasterFieldDef
  onUsedInTemplateError: (templates: Array<ErrorTemplateRef>) => void
}> = ({ masterUuid, fieldDef, onUsedInTemplateError }) => {
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { mutate, isPending } = useMutationDeleteMasterFieldDef(masterUuid)
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()

  const showConfirm = useCallback(() => setConfirmOpen(true), [])
  const closeConfirm = useCallback(() => setConfirmOpen(false), [])

  const deleteFieldDef = useCallback(() => {
    mutate(fieldDef.code, {
      onSuccess: () => {
        closeConfirm()
        showSuccessSnackbar(`「${fieldDef.name}」を削除しました`)
      },
      onError: (error: unknown) => {
        closeConfirm()
        if (isHTTPError(error)) {
          switch (error.status) {
            case 400:
            case 404:
            case 409:
              if (
                error.code ===
                ErrorCodeEnum.CannotDeleteMasterFieldDefUsedInTemplateError
              ) {
                onUsedInTemplateError(
                  (error as HTTPErrorWithTemplates).templates ?? [],
                )
                return
              }
              showErrorSnackbar(error.errors[0])
              return
            default:
              break
          }
        }
        showErrorSnackbar(`「${fieldDef.name}」の削除に失敗しました`)
      },
    })
  }, [
    fieldDef,
    mutate,
    showSuccessSnackbar,
    showErrorSnackbar,
    closeConfirm,
    onUsedInTemplateError,
  ])

  const handleConfirmDialogClose = useModalClosePreventionIsPending(
    closeConfirm,
    isPending,
  )

  return (
    <>
      <MenuItem onClick={showConfirm} sx={{ width: 220, color: 'error.main' }}>
        <ListItemIcon>
          <DeleteOutlinedIcon sx={{ color: 'error.main' }} />
        </ListItemIcon>
        <ListItemText>削除</ListItemText>
      </MenuItem>
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        aria-labelledby="delete-confirm-dialog-title"
      >
        <DialogTitle id="delete-confirm-dialog-title">
          「{fieldDef.name}」を削除してもよろしいですか？
        </DialogTitle>
        <DialogActions>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={closeConfirm}
              disabled={isPending}
              color="secondary"
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={deleteFieldDef}
              color="error"
              loading={isPending}
            >
              削除
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  )
}
